import { Info, Clock, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import BackBtn from "../../Button/BackBtn";
import OverviewPatientDataTable from "../../DataTable/OverviewPatientDataTable";
import DataTablePagination from "../../DataTablePagination";
import Filter from "../../Filter";
import Spacing from "../../Spacing";
import FilterSummary from '../../UI/FilterSummary';
import { exportPatients, fetchPatients, fetchVisitedPatients } from "../../../services/patient";
import { can_visit, debounce, generatePatientTableData, getDateString, getPractice, getSpeciality, notify, numberWithCommas, userMessageTemplates } from "../../../helpers";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import HaventVisitCard from "../../Card/HaventVisitAndPreventativeDueCard";
import { fetchFilterData } from "../../../services/saveFilter";
import SaveFilter from "../../Filter/SaveFilter";
import FilterSaveConfirmationModal from "../../Modal/FilterSaveConfirmationModal";
import { useNavigate } from "react-router-dom";
import { addToWaitingRoom } from "../../../services/patients";

export default function PreventativeDue({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, waitingRoomProviders}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }
  let dateStr = getDateString();

  const navigate = useNavigate();
  const [buttonLoader, setButtonLoader] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [messageTemplate] = useState(userMessageTemplates());
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  let revenuePerPatient = 350;
  // calucalate number of days till now in this year

  const dayOfYear = Math.floor(
    (new Date() - new Date(new Date().getFullYear(), 0, 1)) /
      (1000 * 60 * 60 * 24)
  );

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    last_visit: "",
    visit_day_query_type: "2",
    visited_last_days: "",
    not_visited_last_days: dayOfYear,
    risk_level: "",
    preventative_due: "",
    skip_self_pay: true,
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);

  const static_filter = {
    visit_day_query_type: "2",
    not_visited_last_days: dayOfYear,
    skip_self_pay: true,
  };

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });
  
  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);
  const [patientFormData, setPatientFormData] = useState(filter);
  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const [visitedDataCount, setVisitedDataCount] = useState({
    totalCount: 0,
  });

  const havenotVistedPatients =parseInt(dataCount?.totalCount) || 0;
  const visitedPatients = parseInt(visitedDataCount?.totalCount) || 0;
  const totalPatients = visitedPatients + havenotVistedPatients;
  const calculatePercentageOfVisitedPatients =((visitedPatients / totalPatients)*100) || 0;
  const percentageOfVisitedPatients = isNaN(calculatePercentageOfVisitedPatients) ? "0.00" : calculatePercentageOfVisitedPatients?.toFixed(2);
  const havenotVisitThisYear = havenotVistedPatients - visitedPatients;

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchPatients(getFilter);
      if (response) {
        const generateData = generatePatientTableData(response?.data?.results);
        setData(generateData);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadHaveNotVisitPatientData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };
  
  useEffect(() => {
    loadData(filter);
    loadPatientVisitedData(filter)
  }, [filter]);

  const loadPatientVisitedData = async (getFilter) => {
    setIsLoading(true);
    try {
      const response = await fetchVisitedPatients(getFilter);

      if (response) {
        setVisitedDataCount({
          totalCount: response.data.count,
        });
      }
    } catch (error) {
      // console.log("🚀 ~ loadPatientVisitedData ~ error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const [isFilterCleared, setIsFilterCleared] = useState(false);

  const handleClearFilters = () => {
  setIsFilterCleared(true);
  };

  const handleResetFilterStatus = () => {
  setIsFilterCleared(false);
  };

  // Handles the checkbox change event for a specific patient
  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  // Handles the select all functionality for data

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  // Handles the page change event
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  // Handles the change event for the search term input field
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handles the change of rows per page in the filter
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const handleExportAll = async (e) => {
    setButtonLoader(true);
    try {
      const response = await exportPatients(filter);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Patient_Data_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
    } catch (error) {
      // console.log("🚀 ~ ExporPatients ~ error:", error);
    } finally {
      setButtonLoader(false);
    }
  };

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",
    }));
  };

  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "haven-not-visited-in-one-year",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const handleAddToWaitingRoom = async (data, id) => {
    const postDataObject = {
      account_id: account_id,
      location_id: location_id,
      patient_id: data.id,
      speciality_id: specilityId,
      dos: dateStr,
      care_gap: [],
      review_sent: false,
      provider_id: id
    };

    try {
      await addToWaitingRoom(postDataObject);
      notify("success", "Patient added to waiting room successfully");
    } catch (error) {
      notify("error", error?.response?.data?.detail);
    }
  };

  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  useDocumentTitle("Haven’t visited this year");

  return (
    <>
      <BackBtn href="/" btnText="Back to home" />
      <Spacing lg="0" sm="24" />
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#067a6f" }}
          >
            <Clock width={18} height={18} />
          </i>
          Haven't Visited in This Year
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
        <div className="docpro_page_heading_btns"></div>
      </div>
      {(can_visit('dashboard_overview_havent_visited') && can_visit('overview_have_not_visit')) && (
        <>
          <HaventVisitCard 
            topLeftElement={<><span style={{color: 'var(--vivid)'}}>{numberWithCommas(visitedPatients)}</span> patients visited in this year.</>} 
            topRightElement={<><span style={{color: 'var(--vivid)'}}>{numberWithCommas(havenotVisitThisYear)}</span> patients haven't visited this year.</>} 
            percentage ={percentageOfVisitedPatients}
            percentText ={<span style={{ whiteSpace: 'nowrap' }}>Visited Patients: {percentageOfVisitedPatients}%</span>}
            bottomLeftElement={<p><span style={{color: 'var(--vivid)',fontWeight:'900'}}>${numberWithCommas((visitedPatients*revenuePerPatient))}</span> potential revenue earned this year from visited patients </p>}
            bottomRightElement={<p>
              <span style={{color: 'var(--vivid)',fontWeight:'900'}}>${numberWithCommas((havenotVistedPatients*revenuePerPatient))}</span> revenue missed this year from visited patients&nbsp;&nbsp;
              <span className="docpro_highrisk_tooltip message_header_tooltip relative">
                <Info height={13} width={13} style={{marginBottom:'3px'}} />
                <span className="docpro_tip">Estimated revenue per patient: ${revenuePerPatient}</span>
              </span>
            </p>} 
            isLoading = {isLoading}
          />
          <Spacing lg="20" sm="20" />
        </>
      )}
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form width_100">
            <input
              type="search"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>

          <div className="docpro_data_table_btns small_filter_btns3 p-0">
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadData}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <Filter
              filterType="patient"
              reloadData={loadData}
              reloadDataFilter={filter}
              staticFilter={static_filter}
              setDataLoading={setDataLoading}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              selectedInsuranceOptions={selectedInsuranceOptions}
              setSelectedInsuranceOptions={setSelectedInsuranceOptions}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              setShowSave={setShowSave}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={{
              ...filter,
              not_visited_last_days: undefined,
            }}
            setFilter={setFilter}
            loadData={loadData}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setPreviousScrollPosition={setPreviousScrollPosition}
            setDataLoading={setDataLoading}
          /> 
        </div>
        <OverviewPatientDataTable
          tableData={data}
          handleSelectAll={() => handleSelectAll(data)}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          exportData={handleExportAll}
          buttonLoader={buttonLoader}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          setLetterLimit={setLetterLimit}
          handleAddToWaitingRoom={handleAddToWaitingRoom}
          handleReferralNavigation={handleReferralNavigation}
          user_id={user_id}
          waitingRoomProviders={waitingRoomProviders}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="haven-not-visited-in-one-year"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
